import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React from "react";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { useNavigate } from "react-router-dom";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

import { ChatType } from "@api/graphql/types-and-hooks";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { primaryRelationship } from "@pages/FronteraClarity/utils/chatCaregiver";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";

interface ChatDetailHeaderProps {
  chat: ChatType;
  sidebarOpen?: boolean;
  /** Omit to hide the sidebar toggle. */
  onToggleSidebar?: () => void;
  setSelectedChat?: React.Dispatch<React.SetStateAction<ChatType | undefined>>;
  /** Overrides the mobile back-button destination. */
  backPath?: string;
}

export const ChatDetailHeader: React.FC<ChatDetailHeaderProps> = ({
  chat,
  sidebarOpen,
  onToggleSidebar,
  setSelectedChat,
  backPath = "/frontera-clarity/expert-chat",
}) => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();

  const learnerFirstName = chat.learner.learnermetadata?.firstName;
  const learnerLastName = chat.learner.learnermetadata?.lastName;

  const relationship = primaryRelationship(chat);
  const caregiverFirstName = relationship?.caregiver.caregivermetadata?.firstName;
  const caregiverLastName = relationship?.caregiver.caregivermetadata?.lastName;
  const relationshipTo = relationship?.socialRelationship;

  return (
    <div className={clsx("flex flex-row items-center px-4 py-2 bg-white border", !isMobile && "rounded-t-md")}>
      {isMobile && setSelectedChat && (
        <button
          type="button"
          className="mr-3"
          onClick={() => {
            navigate(backPath, { replace: true });
            setSelectedChat(undefined);
          }}
        >
          <ArrowLeftIcon className="h-6 text-text-secondary" />
        </button>
      )}

      <UserInitialsView
        className="h-10! w-10! min-h-10! min-w-10! text-[16px]! leading-4"
        firstName={caregiverFirstName || ""}
        lastName={caregiverLastName || ""}
      />

      <div className="flex flex-col h-full pl-2">
        <Paragraph colorType="primary" displayType="loud">
          {caregiverFirstName} {caregiverLastName}
        </Paragraph>
        <div className="flex flex-row items-center gap-2">
          {relationshipTo ? (
            <Paragraph>
              {relationshipTo} to {learnerFirstName} {learnerLastName}
            </Paragraph>
          ) : (
            <Paragraph>
              Client: {learnerFirstName} {learnerLastName}
            </Paragraph>
          )}
        </div>
      </div>

      {onToggleSidebar && (
        <div className="ml-auto">
          {isMobile ? (
            <button onClick={onToggleSidebar}>
              <Paragraph className="text-rio-600!" displayType="loud" size="medium">
                Info
              </Paragraph>
            </button>
          ) : (
            <>
              {sidebarOpen ? (
                <GoSidebarCollapse className="cursor-pointer w-5 h-5 text-text-secondary" onClick={onToggleSidebar} />
              ) : (
                <GoSidebarExpand className="cursor-pointer w-5 h-5 text-text-secondary" onClick={onToggleSidebar} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
